import { Box, Button, Divider, Popover, Stack, useTheme, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { B4Calendar } from '@e3dc-react/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { DateRangeValue } from './date-range-value'
import { RangeKeyDict, DateRange as ReactDateRange } from 'react-date-range'
import { format, locale } from '../../Libs/date'
import 'react-date-range/dist/styles.css' // required per react-date-range docs
import 'react-date-range/dist/theme/default.css' // required per react-date-range docs
import i18next from 'i18next'

const useStyles = makeStyles()(theme => ({
  filterButtonActive: {
    '&.MuiButton-outlined': {
      backgroundColor: theme.palette.filter.activeBackground,
      borderColor: theme.palette.filter.activeBorder,
    },
    '&:hover': {
      backgroundColor: theme.palette.filter.activeBackground,
    },
  },
  filterButtonInactive: {
    backgroundColor: theme.palette.filter.inactiveBackground,
    borderColor: theme.palette.filter.inactiveBorder,
    '&:hover': {
      borderColor: theme.palette.filter.hoverBorder,
      backgroundColor: theme.palette.filter.inactiveBackground,
    },
  },
  filterButtonMenuOpen: {
    borderColor: theme.palette.filter.activeBorder,
  },
  dateRange: {
    /* current day is always marked with a default blue underline, regardless of "color" prop passed to DateRange, so we override it manually */
    '.rdrDayToday': {
      '.rdrDayNumber': {
        span: {
          ':after': {
            background: theme.palette.primary.main,
          },
        },
      },
    },
    '.rdrDayDisabled': {
      /* by default, disabled dates have "cursor: not-allowed" set which is overemphasized. Dates being greyed-out and not showing colored outline on
       * hover is more than enough to indicate that they are disabled.
       */
      cursor: 'inherit',
    },
  },
  menu: {
    margin: 0,
    padding: 0,
  },
  calendarIcon: {
    marginLeft: theme.spacing(1),
    width: '18px',
    height: '18px',
  },
  activeText: {
    color: theme.palette.filter.activeText,
    fontWeight: theme.shape.dateRange.activeTextFontWeight,
  },
  inactiveText: {
    color: theme.palette.text.primary,
  },
  filterButtonText: {
    whiteSpace: 'nowrap' /* prevent separator " - " between start and end date label from breaking into new row */,
  },
}))

export interface DateRangeFilterButtonProps {
  title: string | React.ReactNode
  value: DateRangeValue | undefined
  onChange: (dateRange: DateRangeValue | undefined) => void
  minDate?: Date
  maxDate?: Date
}

/*
 * react-date-range supports multiple ranges to be selected at the same time in the calendar. Each can be assigned a different color
 * and changed independently. Each range should be given a unique key in order to idenfity it in the "onChange" event.
 *
 * Our use case requires only one range to be selected at a time, but we still need to assign it a key. To be safe when reading and updating
 * the selection, we use a hardcoded key for this single - main - range. Keeping the default "selection" key for easier comparison to official docs.
 */
const MAIN_DATE_RANGE_KEY = 'selection'

const EMPTY_RANGE: DateRangeValue = {
  startDate: undefined,
  endDate: new Date(0) /* start of UNIX time. Workaround since setting "endDate" to undefined marks all days as "selected" initially */,
}

/*
 * This component renders a button styled as the rest of the filters used for FE table filtering.
 * On click, a popover containing a calendar is opened over the filter button and this is where the
 * user can select a date range.
 *
 * The filter does not immediatelly trigger an "onChange" event, but instead waits for the user to commit to the
 * selection by clicking the "Apply" button in the popover. This keeps the number of background re-renders minimal.
 *
 * To implement this, a local "dateRange" state is used to update the calendar UI independently before triggering the
 * "onChange" event.
 */
export const DateRangeFilterButton: React.FC<DateRangeFilterButtonProps> = ({ title, value, minDate, maxDate, onChange }) => {
  const { t } = useCustomTranslation()
  const { classes, cx } = useStyles()
  const { palette } = useTheme()
  /* besider the "value" prop, we keep local "dateRange" state to update calendar UI with our selection before commiting the selection and closing the picker */
  const [selectedRange, setSelectedRange] = useState<DateRangeValue | undefined>(value || EMPTY_RANGE)
  const displayedRange = selectedRange || EMPTY_RANGE
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const buttonText = value
    ? `${format(new Date(value.startDate!), t('common.format.date-fns-date'))} - ${format(
        new Date(value.endDate!),
        t('common.format.date-fns-date')
      )}`
    : title

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }, [])

  const closePicker = useCallback((): void => {
    setAnchorEl(null)
  }, [])

  const handleChange = useCallback((item: RangeKeyDict): void => {
    const range = item[MAIN_DATE_RANGE_KEY]
    if (range) {
      const { startDate, endDate } = range

      if (startDate && endDate) {
        setSelectedRange({
          startDate,
          endDate,
        })
      }
    }
  }, [])

  const localeLanguageMapper: Record<string, Locale> = {
    de: locale['de'],
    en: locale['enGB'],
    fr: locale['fr'],
    nl: locale['nl'],
    it: locale['it'],
    pl: locale['pl'],
    pt: locale['pt'],
    es: locale['es'],
    sv: locale['sv'],
    el: locale['el'],
    'gsw-FR': locale['frCH'],
    'gsw-CH': locale['de'],
  }

  const handleClear = useCallback((): void => {
    setSelectedRange(EMPTY_RANGE)
    onChange(undefined)
    closePicker()
  }, [closePicker, onChange])

  const handleApply = useCallback((): void => {
    /*
     * "Apply" takes local date range which is displayed in the calendar UI and fires it via "onChange" event to the parent component.
     * However, for the case of initial render and empty selection workaround, the way to detect that nothing is selected yet OR that the selection
     * is cleared is to check for the "startDate" property of the local "dateRange" state and compare it to null. In that case, we will fire "onChange"
     * with "undefined" as value.
     */
    if (!selectedRange?.startDate) {
      onChange(undefined)
    } else {
      onChange(selectedRange)
    }

    closePicker()
  }, [closePicker, selectedRange, onChange])

  useEffect(() => {
    /* since we use "duplicated" local state for the calendar UI, we need to sync it to the main "value" prop */
    if (!value) {
      setSelectedRange(EMPTY_RANGE)
    }
  }, [value])

  const menuOpen = Boolean(anchorEl)

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        disableRipple
        className={cx(classes.filterButtonInactive, {
          [classes.filterButtonActive]: Boolean(value),
          [classes.filterButtonMenuOpen]: menuOpen,
        })}
      >
        <Box>
          <Typography
            variant="caption"
            className={cx(classes.filterButtonText, classes.inactiveText, {
              [classes.activeText]: Boolean(value) || menuOpen,
            })}
          >
            {buttonText}
          </Typography>
        </Box>
        <B4Calendar
          className={cx(classes.calendarIcon, classes.inactiveText, {
            [classes.activeText]: Boolean(value) || menuOpen,
          })}
        />
      </Button>

      <Popover
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorPosition={{
          top: anchorEl ? anchorEl.getBoundingClientRect().bottom : 0,
          left: anchorEl ? anchorEl.getBoundingClientRect().left : 0,
        }}
        PaperProps={{
          className: classes.menu,
        }}
        anchorReference="anchorPosition"
      >
        <Box className={classes.dateRange}>
          <ReactDateRange
            rangeColors={[palette.primary.main]}
            color={palette.primary.main}
            minDate={minDate}
            maxDate={maxDate}
            ranges={[
              {
                startDate: displayedRange.startDate,
                endDate: displayedRange.endDate,
                key: MAIN_DATE_RANGE_KEY,
              },
            ]}
            onChange={handleChange}
            showDateDisplay={false}
            locale={localeLanguageMapper[i18next.language]}
          />

          <Divider sx={{ borderColor: 'divider' }} />

          <Stack direction="row" px={3} py={4} alignItems="center" spacing={1.5}>
            <Button size="small" variant="text" color="inherit" onClick={handleClear}>
              {t('common.clear')}
            </Button>
            <Button size="small" color="primary" variant="contained" onClick={handleApply}>
              {t('common.apply')}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}
